import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { saveAs } from "file-saver";
import { AppThunk, RootState } from "@store";
import {
  HeatmapReportClient,
  IHeatmapReportClient,
  GenerateClusteredHeatmapDataCommand,
  DownloadClusteredHeatmapCommand,
} from "app/ApiClient";
import { BASE_URL } from "const/baseUrl";
import Heatmap from "types/Heatmap/Heatmap";
import Point from "types/Heatmap/Point";
import { HeatmapBaseState } from "..";
import { LoadingState } from "types";

const reportClient: IHeatmapReportClient = new HeatmapReportClient(BASE_URL);

export const downloadHeatmapCsv =
  (arg: DownloadClusteredHeatmapCommand & { clustered?: boolean }, filename: string): AppThunk =>
  async (dispatch, getState) => {
    const { reportSettings } = getState().heatmapReportSettings;
    dispatch(setcsvDownloadingState(true));

    if (arg.clustered) {
      const response = await reportClient.downloadClusteredHeatmap({
        ...arg,
        useBackup: reportSettings?.isBackup,
      });
      saveAs(response.data, filename);
    } else {
      const response = await reportClient.downloadHeatmap({
        ...arg,
        projectId: arg.projectId,
        environmentKey: arg.environmentKey,
        useBackup: reportSettings?.isBackup,
      });
      saveAs(response.data, filename);
    }
    dispatch(setcsvDownloadingState(false));
  };

export const generateHeatmapReport = createAsyncThunk(
  "reports/heatmaps/generate",
  async (
    arg: GenerateClusteredHeatmapDataCommand & {
      clustered: boolean;
      width: number;
      height: number;
      radius: number;
      maxOpacity: number;
      minOpacity: number;
    },
    { signal, getState },
  ) => {
    let heatmap = null;
    const { reportSettings } = (getState() as RootState).heatmapReportSettings;

    if (arg.clustered) {
      heatmap = await reportClient.generateClusteredHeatmap({
        ...arg,
        useBackup: reportSettings?.isBackup
      });
    } else {
      heatmap = await reportClient.generateHeatmap({
        ...arg,
        useBackup: reportSettings?.isBackup,
      });
    }
    if (signal.aborted) {
      throw new Error("Request aborted");
    }
    heatmap.width = arg.width;
    heatmap.height = arg.height;
    heatmap.radius = arg.radius;
    heatmap.maxOpacity = arg.maxOpacity;
    heatmap.minOpacity = arg.minOpacity;
    return heatmap;
  },
);

export interface GenerateHeatmapReportState extends HeatmapBaseState {
  heatmap: Heatmap | null;
  originalPoints: Point[];
  isCsvDownloading: boolean;
  loading: LoadingState;
}

const initialState: GenerateHeatmapReportState = {
  heatmap: null,
  originalPoints: [],
  loading: LoadingState.idle,
  isCsvDownloading: false
}

const generateHeatmapReportSlice = createSlice({
  name: "generateHeatmapReport",
  initialState: initialState,
  reducers: {
    setHeatmapRadius: (state, action: PayloadAction<number>) => {
      if (state.heatmap) {
        state.heatmap.radius = action.payload;
      }
    },
    setHeatmapTransparency: (state, action: PayloadAction<number>) => {
      if (state.heatmap) {
        state.heatmap.maxOpacity = action.payload;
      }
    },
    resetHeatmapReport: (state) => {
      state.heatmap = null;
      state.originalPoints = [];
      state.loading = LoadingState.idle;
    },
    setcsvDownloadingState: (state, action: PayloadAction<boolean>) => {
      state.isCsvDownloading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(generateHeatmapReport.pending, (state) => {
      state.loading = LoadingState.pending;
    });

    builder.addCase(generateHeatmapReport.fulfilled, (state, action) => {
      state.heatmap = new Heatmap(action.payload);
      state.originalPoints =
        action.payload.points?.map((point) => ({
          ...point,
          value: point.value ?? 0,
          x: point.x ?? 0,
          y: point.y ?? 0,
        })) ?? [];
      state.loading = LoadingState.succeed;
    });
  },
});

export const { setHeatmapRadius, setHeatmapTransparency, setcsvDownloadingState, resetHeatmapReport } = generateHeatmapReportSlice.actions;

export default generateHeatmapReportSlice.reducer;
